/** Operator Desk Attention / work deep-link query shape for email / Telegram / push. */

import { parseOperatorDeskWorkItemId } from './operatorDeskWork';
import type { DeskAttentionKind } from './operatorDeskOverview';

export const OPERATOR_DESK_QUERY = {
  desk: 'desk',
  tab: 'desk_tab',
  work: 'work',
  kind: 'attention_kind',
} as const;

export const OPERATOR_DESK_DEEP_TABS = [
  'approvals',
  'attention',
  'timeline',
  'projects',
  'assign',
  'map',
  'comms',
  'payouts',
] as const;

export type OperatorDeskDeepTab = (typeof OPERATOR_DESK_DEEP_TABS)[number];

export type OperatorDeskDeepLink = {
  workId: string;
  tab: OperatorDeskDeepTab;
  kind?: DeskAttentionKind;
};

const DEFAULT_ORIGIN = 'https://ziawellnav.com';
const TAB_SET = new Set<string>(OPERATOR_DESK_DEEP_TABS);

/** Prefer desk_tab=payouts; desk_tab=escrow remains a redirect/alias for old links. */
export function normalizeOperatorDeskDeepTab(value: unknown): OperatorDeskDeepTab | null {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'requests') return 'projects';
  if (raw === 'escrow') return 'payouts';
  if (TAB_SET.has(raw)) return raw as OperatorDeskDeepTab;
  return null;
}

export function isOperatorDeskWorkId(value: unknown): value is string {
  const id = String(value || '').trim();
  if (!id || id.length > 120) return false;
  return /^general:[a-zA-Z0-9_-]{1,100}$/.test(id) || Boolean(parseOperatorDeskWorkItemId(id));
}

export function isOperatorDeskDeepTab(value: unknown): value is OperatorDeskDeepTab {
  return normalizeOperatorDeskDeepTab(value) != null;
}

/** Parse `?desk=1&desk_tab=timeline&work=haul:…&attention_kind=schedule`. */
export function parseOperatorDeskDeepLink(
  search: string | URLSearchParams | null | undefined,
): OperatorDeskDeepLink | null {
  let params: URLSearchParams;
  try {
    if (search instanceof URLSearchParams) params = search;
    else {
      const raw = String(search || '');
      params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
    }
  } catch {
    return null;
  }
  const desk = String(params.get(OPERATOR_DESK_QUERY.desk) || '').trim();
  const workId = String(params.get(OPERATOR_DESK_QUERY.work) || '').trim();
  const tabRaw = String(params.get(OPERATOR_DESK_QUERY.tab) || '').trim().toLowerCase();
  const kindRaw = String(params.get(OPERATOR_DESK_QUERY.kind) || '').trim().toLowerCase();
  if (desk !== '1' && desk.toLowerCase() !== 'true') return null;
  if (!isOperatorDeskWorkId(workId)) return null;
  const tab: OperatorDeskDeepTab = normalizeOperatorDeskDeepTab(tabRaw) || 'attention';
  const kind = (
    ['blocked', 'approval', 'schedule', 'contractor', 'funding', 'evidence'] as DeskAttentionKind[]
  ).includes(kindRaw as DeskAttentionKind)
    ? (kindRaw as DeskAttentionKind)
    : undefined;
  return { workId, tab, kind };
}

export function buildOperatorDeskDeepLinkPath(input: {
  workId: string;
  tab: OperatorDeskDeepTab;
  kind?: DeskAttentionKind | null;
}): string | null {
  if (!isOperatorDeskWorkId(input.workId) || !isOperatorDeskDeepTab(input.tab)) return null;
  const params = new URLSearchParams();
  params.set(OPERATOR_DESK_QUERY.desk, '1');
  params.set(OPERATOR_DESK_QUERY.tab, input.tab);
  params.set(OPERATOR_DESK_QUERY.work, input.workId);
  if (input.kind) params.set(OPERATOR_DESK_QUERY.kind, input.kind);
  return `/?${params.toString()}`;
}

export function buildOperatorDeskDeepLinkUrl(
  env: { PUBLIC_APP_URL?: string; APP_BASE_URL?: string } | null | undefined,
  input: { workId: string; tab: OperatorDeskDeepTab; kind?: DeskAttentionKind | null },
  requestUrl?: string,
): string | null {
  const path = buildOperatorDeskDeepLinkPath(input);
  if (!path) return null;
  let base = String(env?.PUBLIC_APP_URL || env?.APP_BASE_URL || '').replace(/\/$/, '');
  if (!base && requestUrl) {
    try {
      base = new URL(requestUrl).origin;
    } catch {
      base = '';
    }
  }
  if (!base) base = DEFAULT_ORIGIN;
  return `${base}${path}`;
}

/** Session key VoiceNavigation / LandingPage use after sign-in. */
export const ZIA_OPEN_OPERATOR_DESK_KEY = 'zia_open_operator_desk';

/** Consume Funding as a navigation intent; checkout receipt params stay for reconciliation. */
export function parseOperatorDeskFundingDeepLink(
  search: string | URLSearchParams | null | undefined,
): { tab: 'funding' } | null {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search || '');
  const desk = String(params.get(OPERATOR_DESK_QUERY.desk) || '').trim().toLowerCase();
  const tab = String(params.get(OPERATOR_DESK_QUERY.tab) || '').trim().toLowerCase();
  if (desk !== '1' && desk !== 'true') return null;
  const fundingReturn = ['0', '1'].includes(params.get('company_funded') || '');
  // Older company-deposit checkout links returned to Payouts / Escrow.
  if (tab === 'funding' || (fundingReturn && ['payouts', 'escrow'].includes(tab))) {
    return { tab: 'funding' };
  }
  return null;
}


export const OPERATOR_DESK_PAYOUTS_TAB = 'payouts' as const;
export const OPERATOR_DESK_PAYOUTS_TAB_ALIAS = 'escrow' as const;

export type OperatorDeskPayoutsDeepLink = {
  tab: typeof OPERATOR_DESK_PAYOUTS_TAB;
  workId: string | null;
};

/** Parse `?desk=1&desk_tab=payouts|escrow` (work optional). Escrow aliases to Payouts. */
export function parseOperatorDeskPayoutsDeepLink(
  search: string | URLSearchParams | null | undefined,
): OperatorDeskPayoutsDeepLink | null {
  let params: URLSearchParams;
  try {
    if (search instanceof URLSearchParams) params = search;
    else {
      const raw = String(search || '');
      params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
    }
  } catch {
    return null;
  }
  const desk = String(params.get(OPERATOR_DESK_QUERY.desk) || '').trim();
  const tabRaw = String(params.get(OPERATOR_DESK_QUERY.tab) || '').trim().toLowerCase();
  const workIdRaw = String(params.get(OPERATOR_DESK_QUERY.work) || '').trim();
  if (desk !== '1' && desk.toLowerCase() !== 'true') return null;
  if (tabRaw !== OPERATOR_DESK_PAYOUTS_TAB && tabRaw !== OPERATOR_DESK_PAYOUTS_TAB_ALIAS) return null;
  const workId = workIdRaw && isOperatorDeskWorkId(workIdRaw) ? workIdRaw : null;
  if (workIdRaw && !workId) return null;
  return { tab: OPERATOR_DESK_PAYOUTS_TAB, workId };
}

export function buildOperatorDeskPayoutsPath(workId?: string | null): string {
  const params = new URLSearchParams();
  params.set(OPERATOR_DESK_QUERY.desk, '1');
  params.set(OPERATOR_DESK_QUERY.tab, OPERATOR_DESK_PAYOUTS_TAB);
  if (workId && isOperatorDeskWorkId(workId)) {
    params.set(OPERATOR_DESK_QUERY.work, workId);
  }
  return `/?${params.toString()}`;
}
